/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends From
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { action, computed } from '@ember/object';
// External Imports
import moment from 'moment';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Options from '@linc-technologies/linced-common/enums/options';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
// Internal Imports
import LearnerFlag from 'linced-app/enums/learner-flag';
import PageType from 'linced-app/enums/page-type';

export default class PersonCard extends Component {
  // Services
  @service
  features;

  @service
  options;

  @service
  print;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  // Attrs
  LearnerFlag = LearnerFlag;

  PersonType = PersonType;

  Scopes = Scopes;

  Jurisdiction = Jurisdiction;

  @optionAlias(Options.REPORTING_REPORT_PAGE_NAME)
  reportPageName;

  // currentYear sets the current year, as of now.
  currentYear = moment().year();

  // progressionsEnabled specifies whether progress reporting has been enabled
  // by the school.
  @optionAlias(Options.PROGRESS_REPORTING_ENABLED)
  progressionsEnabled;

  // Props
  // numTermsToShow specifies the number of terms data to load in the
  // assessment/progressions overview.
  numTermsToShow = 8;

  // person provides the person model to display information for.
  person = null;

  // onClose provides a function to be called when the person card is closed.
  onClose() {}

  // State
  // activeTab provides the state for the current tab selected in the person
  // card.
  @tracked
  activeTab = 0;

  @action
  setActiveTab(value) {
    this.activeTab = value;
  }

  // learnerFlagUpdating provides the state for whether a flag is being set on
  // a learner while updating the underlying person record.
  @tracked
  learnerFlagUpdating = false;

  // pages provides the state of visible pages for the given person.
  @tracked
  pages = [];

  // generatingProfile flags whether a Profile Sheet is being generated.
  @tracked
  generatingProfile = false;

  // generatingProfile flags whether a Medical Sheet is being generated.
  @tracked
  generatingMedical = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  badges;

  @computed('jurisdiction')
  get jurisdictionIsUK() {
    return this.jurisdiction === Jurisdiction.UK;
  }

  @computed(
    'person.{id,type}',
    'roles.currentUserIsRestricted',
    'session.person.id',
  )
  get hasAccess() {
    if (this.get('session.person.id') === this.get('person.id')) {
      return true;
    }
    return !this.roles.currentUserIsRestricted;
  }

  // reportTitle computes a dynamic report title based on the date range.
  @computed('reportPageName')
  get reportTitle() {
    return this.reportPageName || `${moment().year()} Report`;
  }

  // Methods
  @action
  close() {
    this.onClose();
    if (!isEmpty(this.extraOnClose)) {
      this.extraOnClose(true);
    }
  }

  @action
  goToPage(person, page) {
    this.close();

    if (page) {
      if (
        page.get('hasAssessments') &&
        !isEmpty(page.get('assessments')) &&
        page.get('assessments').length === 1
      ) {
        this.router.transitionTo(
          'people.person.page.assessment',
          person.get('id'),
          page.get('id'),
          page.get('assessmentIds')[0].id,
        );
      } else if (page.hasPosts && page.goalSubjects.length) {
        this.router.transitionTo(
          'people.person.page.unlinked-posts',
          person.get('id'),
          page.get('id'),
        );
      } else {
        this.router.transitionTo(
          'people.person.page',
          person.get('id'),
          page.get('id'),
        );
      }
    } else {
      this.router.transitionTo('people.person', person.get('id'));
    }
  }

  @action
  goToRoute(route, person) {
    this.close();
    this.router.transitionTo(route, person.get('id'));
  }

  @action
  toggleLearnerFlag(flag, enable) {
    this.learnerFlagUpdating = true;
    this.person
      .reload()
      .then((person) => {
        if (enable) {
          person.learner.flags.pushObject(flag);
        } else {
          person.learner.flags.removeObject(flag);
        }
        return person.save();
      })
      .finally(() => {
        this.learnerFlagUpdating = false;
      });
  }

  @action
  downloadProfileSheet() {
    this.generatingProfile = true;
    this.print.profile(this.person, () => {
      this.generatingProfile = false;
    });
  }

  @action
  downloadMedicalSheet() {
    this.generatingMedical = true;
    this.print.medicalSheet(this.person, () => {
      this.generatingMedical = false;
    });
  }

  // Lifecycle hooks
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    // Work out which pages should display for a person.
    let visiblePages = [];
    let pagesPromise = Promise.resolve([]);
    let personType = this.get('person.type');
    switch (personType) {
      case PersonType.LEARNER:
        pagesPromise = this.store.query('posts/page', {
          type: PageType.STUDENT,
          visibleInMenu: true,
          learnerId: this.get('person.id'),
        });
        break;

      case PersonType.STAFF:
        if (this.hasAccess) {
          visiblePages = this.store
            .peekAll('posts/page')
            .filter((page) => page.type === PageType.STAFF)
            .filter((page) => page.visibleInMenu);
          pagesPromise = Promise.resolve(visiblePages);
        }
        break;
    }

    // Sort and set (once resolved)!
    pagesPromise.then((pages) => {
      this.pages = pages
        .slice()
        .sort(
          (a, b) =>
            a.sequence - b.sequence ||
            (a.label || '').localeCompare(b.label || ''),
        );
    });

    // If badges aren't passed in, grab them :)
    if (this.badges === undefined) {
      this.store
        .query('goals/badge', {
          personId: this.get('person.id'),
        })
        .then((badges) => {
          this.badges = badges;
        });
    }
  }
}
